import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';
import { useAuthorization } from '../../auth/useAuthorization';
import { Role } from '../../auth/roles';
import Button from '../common/Button';

export interface TenantActivityRow {
  tenantName: string;
  sodasCount: number;
  havalasCount: number;
  partiesCount: number;
}

function normalizeRow(raw: Record<string, unknown>): TenantActivityRow {
  return {
    tenantName: String(raw.tenantName ?? raw.TenantName ?? '').trim() || '—',
    sodasCount: Number(raw.sodasCount ?? raw.SodasCount ?? 0) || 0,
    havalasCount: Number(raw.havalasCount ?? raw.HavalasCount ?? 0) || 0,
    partiesCount: Number(raw.partiesCount ?? raw.PartiesCount ?? 0) || 0,
  };
}

function clampDays(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(366, Math.max(0, Math.floor(value)));
}

function clampHours(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(23, Math.max(0, Math.floor(value)));
}

function activityWindowLabel(days: number, hours: number): string {
  const safeDays = clampDays(days);
  const safeHours = clampHours(hours);
  const totalHours = safeDays * 24 + safeHours;
  const shownDays = totalHours < 1 ? 0 : safeDays;
  const shownHours = totalHours < 1 ? 1 : safeHours;
  const parts: string[] = [];
  if (shownDays > 0) parts.push(`${shownDays} day${shownDays === 1 ? '' : 's'}`);
  if (shownHours > 0) parts.push(`${shownHours} hour${shownHours === 1 ? '' : 's'}`);
  return parts.join(' and ');
}

function parseResponse(payload: unknown): TenantActivityRow[] {
  if (Array.isArray(payload)) {
    return payload.map((item) =>
      normalizeRow(typeof item === 'object' && item !== null ? (item as Record<string, unknown>) : {}),
    );
  }
  if (payload && typeof payload === 'object') {
    const o = payload as Record<string, unknown>;
    const inner = o.data ?? o.items ?? o.result;
    if (Array.isArray(inner)) {
      return parseResponse(inner);
    }
  }
  return [];
}

const TenantActivities: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole, isAuthenticated } = useAuthorization();
  const [rows, setRows] = useState<TenantActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(1);
  const [appliedDays, setAppliedDays] = useState(0);
  const [appliedHours, setAppliedHours] = useState(1);

  useEffect(() => {
    if (isAuthenticated === false) {
      navigate('/login', { replace: true });
      return;
    }
    if (isAuthenticated && !hasRole(Role.ADMIN)) {
      window.dispatchEvent(
        new CustomEvent('show-toast', {
          detail: {
            message: 'Only administrators can view tenant activities.',
            type: 'error',
          },
        }),
      );
      navigate('/game/oneday#home', { replace: true });
    }
  }, [isAuthenticated, hasRole, navigate]);

  const fetchCounts = useCallback(async (nextDays: number, nextHours: number) => {
    if (!isAuthenticated || !hasRole(Role.ADMIN)) return;
    const safeDays = clampDays(nextDays);
    const safeHours = clampHours(nextHours);
    setDays(safeDays);
    setHours(safeHours);
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.get<unknown>(apiRoutes.tenants.tenantWiseCounts(safeDays, safeHours));
      setRows(parseResponse(data));
      setAppliedDays(safeDays);
      setAppliedHours(safeHours);
    } catch (e: unknown) {
      const msg =
        (e as { message?: string })?.message ||
        (typeof e === 'object' && e !== null && 'error' in e && typeof (e as { error: unknown }).error === 'string'
          ? (e as { error: string }).error
          : 'Failed to load tenant activity counts.');
      setError(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, hasRole]);

  useEffect(() => {
    if (isAuthenticated && hasRole(Role.ADMIN)) {
      void fetchCounts(0, 1);
    }
  }, [isAuthenticated, hasRole, fetchCounts]);

  if (!isAuthenticated || !hasRole(Role.ADMIN)) {
    return null;
  }

  return (
    <div className="min-h-0 flex-1 overflow-auto bg-slate-100 p-4 dark:bg-slate-950">
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Tenant Activities</h1>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Sodas, Havala entries, and parties per tenant in the last{' '}
              <strong>{activityWindowLabel(appliedDays, appliedHours)}</strong>.
            </p>
          </div>
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              void fetchCounts(days, hours);
            }}
          >
            <label className="text-sm text-slate-700 dark:text-slate-200">
              Days
              <input
                type="number"
                min={0}
                max={366}
                value={days}
                disabled={loading}
                onChange={(event) => setDays(clampDays(Number(event.target.value)))}
                className="mt-1 block w-24 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
            <label className="text-sm text-slate-700 dark:text-slate-200">
              Hours
              <input
                type="number"
                min={0}
                max={23}
                value={hours}
                disabled={loading}
                onChange={(event) => setHours(clampHours(Number(event.target.value)))}
                className="mt-1 block w-24 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
            <Button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            >
              Show activity
            </Button>
            <Button
              type="button"
              onClick={() => void fetchCounts(days, hours)}
              disabled={loading}
              className="inline-flex items-center gap-2 bg-slate-600 text-white hover:bg-slate-700 disabled:opacity-60"
            >
              <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </form>
        </div>

        {error && (
          <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
            {error}
          </div>
        )}

        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow dark:border-slate-700 dark:bg-slate-900">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
            <thead className="bg-slate-50 dark:bg-slate-800">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200"
                >
                  Tenant name
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200"
                >
                  Sodas
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200"
                >
                  Havalas
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200"
                >
                  Parties
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {loading && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && !error && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                    No tenant activity in the last {activityWindowLabel(appliedDays, appliedHours)}.
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((row, idx) => (
                  <tr key={`${row.tenantName}-${idx}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/80">
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-slate-900 dark:text-slate-100">
                      {row.tenantName}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums text-slate-800 dark:text-slate-200">
                      {row.sodasCount}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums text-slate-800 dark:text-slate-200">
                      {row.havalasCount}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums text-slate-800 dark:text-slate-200">
                      {row.partiesCount}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TenantActivities;
